import useAppStore from "@/context/store";
import { searchBoardMembers } from "@/services";
import displayNum from "@/utils/displayNum";
import { useInfiniteList } from "@/utils/hooks/useInfiniteList";
import { useKeyboardNavigation } from "@/utils/hooks/useKeyboardNavigation";
import useReq from "@/utils/useReq";
import { useUpdateEffect } from "ahooks";
import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import HBAvatar from "../Avatar";
import HBLoading from "../HBLoading";
import InfiniteScroll from "../InfiniteScroll";
import styles from "./index.module.less";

const PAGE_SIZE = 20;

export function UserList({
  selecteds = [],
  searchKeyword,
  onSelect,
  maxHeight = 267,
  invitedUsers = [],
  keyboardNavigation = false,
  renderAction,
  withHover = true,
  board,
  ...props
}: {
  selecteds: number[];
  searchKeyword?: string;
  onSelect?: (user: IUser) => void;
  maxHeight?: number;
  invitedUsers?: IUser[];
  keyboardNavigation?: boolean;
  /** 渲染操作按钮 */
  renderAction?: (user: IUser) => React.ReactNode;
  /** 是否不显示hover状态 */
  withHover?: boolean;
  board?: IBoardInfo;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const userItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const currentUser = useAppStore((state) => state.user);
  const board_id = board?.board_id;

  const { runAsync: getUsers } = useReq(
    async (page: number) => {
      if (!searchKeyword) {
        return [];
      }

      const result = await searchBoardMembers({
        nickname: searchKeyword,
        board_id: board_id,
        page,
        limit: PAGE_SIZE,
      });

      const users = ((result?.users as IUser[]) || []).filter(
        // 过滤自己
        (user) => user.user_id !== currentUser?.user_id
      );

      return users;
    },
    {
      manual: true,
      debounceWait: 300,
    }
  );

  const {
    list: dataSource,
    hasMore,
    onLoadMore,
    resetData,
    loading,
  } = useInfiniteList(getUsers, {
    hasMoreFn: (list) => list.length >= PAGE_SIZE,
  });

  useUpdateEffect(() => {
    resetData();
  }, [searchKeyword]);

  const { isActive, activeIndex } = useKeyboardNavigation({
    itemCount: dataSource.length,
    onSelect: (index) => onSelect?.(dataSource[index]),
    circular: true,
    enabled: keyboardNavigation,
  });

  // 当活动项改变时，滚动到对应的元素
  useEffect(() => {
    if (activeIndex >= 0 && activeIndex < userItemRefs.current.length) {
      const activeElement = userItemRefs.current[activeIndex];
      const container = scrollContainerRef.current;

      if (activeElement && container) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = activeElement.getBoundingClientRect();

        // 检查元素是否在视图范围内
        const isElementVisible =
          elementRect.top >= containerRect.top &&
          elementRect.bottom <= containerRect.bottom;

        if (!isElementVisible) {
          // 如果元素不在视图范围内，滚动到元素位置
          activeElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
          });
        }
      }
    }
  }, [activeIndex]);

  // 没有搜索关键词，展示已邀请用户或者空状态
  if (!searchKeyword) {
    if (invitedUsers.length > 0) {
      return (
        <div
          {...props}
          className={styles.userList}
          style={{ maxHeight, ...props.style }}
        >
          {invitedUsers.map((user) => (
            <UserItem
              withHover={withHover}
              key={user.user_id}
              user={user}
              onSelect={onSelect}
              active={false}
              selected={false}
              renderAction={renderAction}
              board={board}
            />
          ))}
        </div>
      );
    }

    return null;
  }

  // loading与空搜索结果
  if (dataSource.length === 0) {
    if (loading) {
      return (
        <div className={styles.loading}>
          <HBLoading size="18px" />
        </div>
      );
    }
    return (
      <div className={styles.empty}>
        <span>暂无搜索结果</span>
      </div>
    );
  }

  // 搜索结果
  return (
    <div
      {...props}
      className={classNames(styles.userList, props.className)}
      id="user-list"
      style={{ maxHeight, ...props.style }}
      ref={scrollContainerRef}
    >
      <InfiniteScroll
        className={styles.container}
        dataLength={dataSource.length}
        hasMore={hasMore}
        next={onLoadMore}
        scrollableTarget={"user-list"}
        loader={
          <div className={styles.loading}>
            <HBLoading size="18px" />
          </div>
        }
        scrollThreshold="100px"
      >
        {dataSource.map((user, index) => (
          <UserItem
            ref={(el) => {
              userItemRefs.current[index] = el;
            }}
            key={index}
            active={isActive(index)}
            selected={selecteds.includes(user.user_id)}
            withHover={withHover}
            user={user}
            onSelect={onSelect}
            renderAction={renderAction}
            board={board}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
}

const UserItem = React.forwardRef<
  HTMLDivElement,
  {
    user: IUser;
    onSelect: (user: IUser) => void;
    /** hover状态 */
    active: boolean;
    selected: boolean;
    withHover?: boolean;
    renderAction?: (user: IUser) => React.ReactNode;
    board?: IBoardInfo;
  }
>(({ user, active, renderAction, withHover, board }, ref) => {
  return (
    <div
      className={classNames(styles.userItem, {
        [styles.active]: active,
        [styles.withHover]: withHover,
      })}
      ref={ref}
    >
      <HBAvatar user={user} size={40} shape="circle" withLink />
      <div className={styles.userInfo}>
        <div className={styles.username}>{user.username}</div>
        <div className={styles.nums}>
          {board?.user_id === user.user_id ? (
            <span>该画板创建者</span>
          ) : (
            <>
              <span>{displayNum(user.board_count)} 画板</span>·
              <span>{displayNum(user.pin_count)} 采集</span>·
              <span>{displayNum(user.follower_count)} 粉丝</span>
            </>
          )}
        </div>
      </div>
      <div>{renderAction(user)}</div>
    </div>
  );
});
